<script setup>
import { ref, onMounted } from 'vue';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Button from './Button.vue';
import CustomLink from './CustomLink.vue';

gsap.registerPlugin(ScrollTrigger);

// Animation refs
const teamSectionRef = ref(null);
const headingRef = ref(null);
const descriptionRef = ref(null);
const teamGridRef = ref(null);
const aboutTextRef = ref(null);

// Updated team members data with the actual team
const teamMembers = [
    {
        name: "Dennis Tuparia",
        role: "Project Manager",
        additional: "Eigenaar",
        photo: "/img/team/dennis.jpeg", 
        specialty: "Project Management & Business Strategy",
        description: "Als eigenaar en projectmanager zorgt Dennis voor de perfecte afstemming tussen de technische mogelijkheden en uw zakelijke doelen.",
        links: {
            linkedin: "https://linkedin.com/in/dennistuparia"
        }
    },
    {
        name: "Emiel Jaspers",
        role: "Lead Developer",
        additional: "Backend Specialist",
        photo: "/img/team/emiel.jpg",
        specialty: "Backend Development & System Architecture",
        description: "Emiel is gespecialiseerd in het bouwen van krachtige en schaalbare backend-systemen die de basis vormen voor complexe applicaties.",
        links: {}
    },
    {
        name: "Tristan Jurjens",
        role: "App Developer",
        additional: "Full Stack",
        photo: "/img/team/tristan.jpg",
        specialty: "Mobile & Cross-Platform Development",
        description: "Tristan bouwt native en hybride apps en heeft uitgebreide kennis van zowel frontend als backend technologieën.",
        links: {}
    },
    {
        name: "Jeroen Huiskes",
        role: "Lead Designer",
        additional: "Frontend Developer",
        photo: "/img/team/jeroen.jpg",
        specialty: "UX/UI Design & Frontend Development",
        description: "Jeroen vertaalt gebruikerswensen naar prachtige, intuïtieve interfaces en brengt deze tot leven met moderne frontend-technologieën.",
        links: {}
    }
];

// Animate elements when the component mounts
onMounted(() => {
  // Animate heading and description - condensed to single lines
  gsap.fromTo(headingRef.value, { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.8, ease: "power2.out", scrollTrigger: { trigger: teamSectionRef.value, start: "top 80%", toggleActions: "play none none none" } });
  gsap.fromTo(descriptionRef.value, { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.8, delay: 0.2, ease: "power2.out", scrollTrigger: { trigger: teamSectionRef.value, start: "top 80%", toggleActions: "play none none none" } });

  // Modified animation for team members - all cards appear at once - condensed to single line
  const teamCards = teamGridRef.value.querySelectorAll('.team-member-card');
  gsap.fromTo(teamCards, { opacity: 0, y: 50, scale: 0.95 }, { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: "back.out(1.2)", scrollTrigger: { trigger: teamGridRef.value, start: "top 80%", toggleActions: "play none none none" } });
  
  // Animate about text section - condensed to single line
  gsap.fromTo(aboutTextRef.value, { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 1, ease: "power2.out", scrollTrigger: { trigger: aboutTextRef.value, start: "top 85%", toggleActions: "play none none none" } });
});
</script>

<template>
  <section ref="teamSectionRef" class="bg-white dark:bg-zinc-900 py-16 md:py-24">
    <div class="wide-container relative">
      <!-- Background decorations with responsive opacity -->
      <div class="absolute inset-0 opacity-5 dark:opacity-10 bg-[linear-gradient(to_right,#0000001a_1px,transparent_1px),linear-gradient(to_bottom,#0000001a_1px,transparent_1px)] dark:bg-[linear-gradient(to_right,#ffffff1a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff1a_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)]"></div>
      
      <div class="relative z-10">
        <!-- Section header with responsive text colors -->
        <div class="text-center mb-16">
          <h2 ref="headingRef" class="text-pretty text-4xl font-bold text-zinc-900 dark:text-white sm:text-5xl lg:text-6xl tracking-tight">
            <span class="bg-text-mint text-transparent bg-clip-text">Ons team</span>
          </h2>
          <p ref="descriptionRef" class="text-xl text-zinc-600 dark:text-zinc-300 max-w-3xl mx-auto mt-6">
            Gedreven professionals die technologie inzetten om uw bedrijf vooruit te helpen. We werken samen als één team om jouw uitdagingen op te lossen.
          </p>
        </div>
        
        <!-- Team members grid that becomes flex on desktop -->
        <div ref="teamGridRef" class="grid grid-cols-1 sm:grid-cols-2 lg:flex gap-6 lg:gap-4 mb-16">
          <div v-for="(member, index) in teamMembers" :key="index" class="team-member-card group relative bg-white/80 dark:bg-zinc-800/80 backdrop-blur-sm border border-zinc-200 dark:border-zinc-700 rounded-xl overflow-hidden shadow-lg dark:shadow-black/30 transition-all duration-700 ease-out hover:shadow-xl lg:flex-1 lg:hover:flex-[1.5]">
            <!-- Member photo with responsive gradient overlay -->
            <div class="relative h-80 lg:h-[32rem]">
              <!-- mint gradient backdrop (always visible) -->
              <div class="absolute inset-0 w-full h-full bg-gradient-mint z-1"></div>
              <!-- Darker overlay that fades in on hover -->
              <div class="dark-overlay absolute inset-0 bg-zinc-900 z-2"></div>
              <img :src="member.photo" :alt="`${member.name} - ${member.role}`" class="absolute inset-0 w-full h-full object-cover object-right transform group-hover:scale-105 group-hover:opacity-35 transition-all duration-500 ease-in-out z-3"/>
              
            </div>
            
            <!-- Member details with improved animations -->
            <div class="absolute bottom-0 left-0 right-0 p-6 text-zinc-900 dark:text-white z-4">
              <!-- Basic info that moves up on hover -->
              <div class="transform transition-transform duration-500 ease-out group-hover:-translate-y-12">
                <p class="text-mint-600 group-hover:text-mint-500 dark:text-mint-600 group-hover:dark:text-mint-900 font-medium text-shadow-md">{{ member.role }}</p>
                <h3 class="text-2xl font-semibold mb-1 text-white">{{ member.name }}</h3>
              </div>
              
              <!-- Extended details with smooth height animation -->
              <div class="max-h-0 opacity-0 group-hover:max-h-48 group-hover:opacity-100 transition-all duration-700 ease-out">
                <div class="pt-4 space-y-2 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500 delay-200 ease-out">
                  <p class="text-sm text-white/95 dark:text-white/30">{{ member.additional }}</p>
                  <p class="text-sm font-medium text-white dark:text-white/90">{{ member.specialty }}</p>
                  <p class="text-sm text-white/95 dark:text-white/30 leading-relaxed">{{ member.description }}</p>
                  <div class="flex gap-3 pt-2">
                    <CustomLink v-if="member.links.linkedin" :href="member.links.linkedin" :useInertia="false" class="h-8 w-8 rounded-full bg-white dark:bg-zinc-700 flex items-center justify-center hover:bg-white/70 hover:dark:bg-zinc-700/70 transition-colors duration-300 cursor-pointer" target="_blank" rel="noopener noreferrer">
                      <font-awesome-icon :icon="['fab', 'linkedin-in']" class="h-4 w-4 text-azure-500 dark:text-azure-500" />
                    </CustomLink>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- About us section with responsive text colors -->
        <div ref="aboutTextRef">
          <div class="max-w-5xl mx-auto text-center mb-6">
            <h3 class="text-2xl md:text-3xl font-bold text-zinc-900 dark:text-white leading-tight">
              Bij c-o-d-e-s combineren we technische expertise met een persoonlijke aanpak. Als partners denken we mee over de lange termijn en bouwen we maatwerkoplossingen die meegroeien met uw bedrijf.
            </h3>
          </div>
          <div class="mt-8 flex flex-col sm:flex-row gap-4 items-center justify-center">
            <Button href="/contact" variant="gradient" icon="users" iconPosition="right">
              Maak kennis met ons team
            </Button>
            <Button href="/over-ons" variant="glass-light" darkVariant="glass" icon="arrow-right" iconPosition="right">
              Meer over c-o-d-e-s
            </Button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Improved team member card animations with width expansion */
.team-member-card {
  transition: transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), 
              box-shadow 0.5s ease,
              background-color 0.3s ease,
              flex 0.7s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Smooth max-height transition for content reveal */
.team-member-card .max-h-0 {
  transition: max-height 0.7s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.7s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Width expansion effect for desktop only */
@media (min-width: 1024px) {
  /* Target the flex container when any child is hovered */
  .lg\\:flex:hover .team-member-card:not(:hover) {
    flex: 0.8 !important;
  }
  
  .lg\\:flex:hover .team-member-card:hover {
    flex: 1.5 !important;
  }
}

/* Enhanced hover glow effect */
.team-member-card::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1;
  opacity: 0;
  border-radius: inherit;
  transition: opacity 0.5s ease-out;
}

.team-member-card:hover::before {
  opacity: 0.8;
  animation: pulse 2s infinite;
  filter: blur(10px);
}

@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.03); opacity: 0.4; }
}

/* Custom smooth fade for dark overlay */
.dark-overlay {
  opacity: 0.2;
  transition: opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.team-member-card:hover .dark-overlay {
  opacity: 1;
}
</style>
